import {
  Body1,
  Body1Strong,
  Caption1,
  ProgressBar,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';

import { AI_FINDINGS_LOADING_STEPS } from '../constants';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  introCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  sectionLabel: {
    color: tokens.colorNeutralForeground3,
  },
  progressMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
  },
  stepList: {
    display: 'grid',
    gap: tokens.spacingVerticalS,
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground4,
  },
  activeStep: {
    color: tokens.colorNeutralForeground2,
  },
  completedStep: {
    color: tokens.colorBrandForeground1,
  },
  stepDot: {
    width: '8px',
    height: '8px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralStrokeAccessible,
    flexShrink: 0,
  },
  activeStepDot: {
    backgroundColor: tokens.colorBrandForeground1,
    boxShadow: `0 0 0 4px ${tokens.colorBrandBackground2}`,
  },
  completedStepDot: {
    backgroundColor: tokens.colorBrandForeground1,
  },
});

type TProps = {
  activeStepIndex: number;
  progress: number;
};

const MockAiFindingsLoadingState = ({ activeStepIndex, progress }: TProps) => {
  const classes = useStyles();
  const currentStep = AI_FINDINGS_LOADING_STEPS[activeStepIndex] ?? AI_FINDINGS_LOADING_STEPS[0];

  return (
    <div className={classes.container}>
      <div className={classes.introCard}>
        <Caption1 className={classes.sectionLabel}>AI review in progress</Caption1>
        <Body1Strong>{currentStep}</Body1Strong>
        <Body1>The findings are being prepared before they are shown in the sidebar.</Body1>
        <div className={classes.progressMeta}>
          <Caption1>Thinking</Caption1>
          <Caption1>{Math.max(1, Math.round(progress * 100))}%</Caption1>
        </div>
        <ProgressBar value={progress} />
      </div>

      <div className={classes.stepList}>
        {AI_FINDINGS_LOADING_STEPS.map((step, index) => {
          const isCompleted = index < activeStepIndex || progress >= 1;
          const isActive = index === activeStepIndex && progress < 1;

          return (
            <div
              key={step}
              className={mergeClasses(
                classes.stepRow,
                isCompleted && classes.completedStep,
                isActive && classes.activeStep
              )}
            >
              <span
                className={mergeClasses(
                  classes.stepDot,
                  isCompleted && classes.completedStepDot,
                  isActive && classes.activeStepDot
                )}
              />
              <Caption1>{step}</Caption1>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MockAiFindingsLoadingState;
